<template>
  <section class="site-filter min-w-0 flex gap-3 items-center justify-center">
    <FilterIcon class="w-5 h-5 fill-blue-gray-800" />
    <p class="font-lato text-sm">
      Filter :
    </p>
    <BaseButton
      class="bg-green-700 hover:bg-green-600 font-lato text-sm text-white rounded-full"
      @click="toggleFilterDropdown"
    >
      <div class="min-w-0 flex gap-2 items-center">
        <p class="font-normal">
          {{ filterButtonLabel }}
        </p>
        <div
          v-show="filterCount"
          class="w-5 h-5 rounded-full bg-red-500 text-white"
        >
          {{ filterCount }}
        </div>
      </div>
      <template #icon-right>
        <JdsIcon
          name="chevron-down"
          size="16px"
          fill="#fff"
          class="h-4 w-4"
        />
      </template>
    </BaseButton>

    <!-- Filter Modal -->
    <BaseModal :open="isFilterOpen">
      <template #header>
        <!-- Title and Reset Button -->
        <div class="min-w-0 w-full p-4 pb-0 flex items-center justify-between">
          <h2 class="font-roboto font-bold text-blue-gray-800">
            Filter Organisasi
          </h2>
          <BaseButton
            class="border-transparent hover:bg-green-50"
            @click="clearFilter"
          >
            <p class="font-normal text-sm text-green-700">
              Hapus semua filter
            </p>
          </BaseButton>
        </div>
      </template>
      <section class="site-filter min-w-[400px]">
        <!-- Organization Select Box -->
        <div
          class="w-full mb-6"
        >
          <div class="min-w-0 w-full flex gap-3 items-center mb-4">
            <p class="font-lato text-sm text-blue-gray-700 leading-none whitespace-nowrap">
              Organisasi
            </p>
            <div class="w-full border border-gray-100" />
          </div>
          <div
            v-if="organizations.length > 0"
            class="w-full"
          >
            <JdsCheckbox
              v-model="checkAllOrganizations"
              :indeterminate="isOrganizationIndeterminate"
              text="Pilih Semua Organisasi"
              class="mb-3"
            />
            <div class="min-w-0 w-full pl-[28px] flex flex-col gap-4">
              <JdsCheckbox
                v-for="(organization, index) in organizations"
                :key="index"
                :text="organization.name"
                :checked="isOrganizationSelected(organization.name)"
                @change="setSelectedOrganization(organization.name)"
              />
            </div>
          </div>
          <div
            v-else
            class="py-12 flex flex-col items-center justify-center gap-2"
          >
            <CategoryIcon />
            <p class="text-blue-gray-300 font-lato text-sm">
              Belum ada organisasi
            </p>
          </div>
        </div>
      </section>
      <template #footer>
        <!-- Cancel and Submit buttons -->
        <div class="w-full flex gap-3">
          <BaseButton
            class="border-green-700 hover:bg-green-50 bg-white w-full"
            @click="closeFilterDropdown"
          >
            <p class="w-full text-sm font-normal text-green-700 text-center">
              Batal
            </p>
          </BaseButton>
          <BaseButton
            class="bg-green-700 hover:bg-green-600 w-full"
            @click="submitFilter"
          >
            <p class="w-full text-sm font-normal text-white text-center">
              Terapkan
            </p>
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </section>
</template>

<script>
import clonedeep from 'lodash.clonedeep';
import CategoryIcon from '@/assets/icons/outline-category.svg?inline';
import FilterIcon from '@/assets/icons/filter.svg?inline';
import BaseButton from '@/common/components/BaseButton';
import BaseModal from '@/common/components/BaseModal';

export default {
  name: 'MicrositeFilter',
  components: {
    CategoryIcon,
    FilterIcon,
    BaseButton,
    BaseModal,
  },
  props: {
    params: {
      type: Object,
      default: () => ({
        organizations: [],
      }),
    },
    data: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isFilterOpen: false,
      filter: {
        organizations: [],
      },
      filterCount: 0,
    };
  },

  computed: {
    organizations() {
      return this.data;
    },
    checkAllOrganizations: {
      get() {
        return this.filter.organizations.length === this.organizations.length;
      },
      set(value) {
        const checked = [];
        if (value) {
          this.organizations.forEach((organization) => {
            checked.push(organization.name);
          });
        }
        this.filter = {
          ...this.filter,
          organizations: checked,
        };
      },
    },

    isOrganizationIndeterminate() {
      if (this.filter.organizations.length && this.filter.organizations.length < this.organizations.length) {
        return true;
      }
      return false;
    },

    filterButtonLabel() {
      return this.filterCount > 0 ? 'Diterapkan' : 'Belum ada filter';
    },
  },

  methods: {
    isOrganizationSelected(organization) {
      return this.filter.organizations.includes(organization);
    },

    setSelectedOrganization(organization) {
      let checked = [];

      if (this.filter.organizations.includes(organization)) {
        checked = this.filter.organizations.filter((item) => item !== organization);
      } else {
        checked = [...this.filter.organizations, organization];
      }

      this.filter = {
        ...this.filter,
        organizations: checked,
      };
    },

    toggleFilterDropdown() {
      this.isFilterOpen = !this.isFilterOpen;
    },

    closeFilterDropdown() {
      this.isFilterOpen = false;
      this.resetFilter();
    },

    submitFilter() {
      const filterObj = clonedeep(this.filter);

      this.$emit('change:filter', filterObj);
      this.updateFilterCount();
      this.toggleFilterDropdown();
    },

    /**
     * Reset filter state to original/actual filter params
     */
    resetFilter() {
      this.filter = {
        organizations: [...this.params.organizations],
      };
    },

    clearFilter() {
      this.filter = {
        organizations: [],
      };
    },

    updateFilterCount() {
      this.filterCount = this.filter.organizations.length ?? 0;
    },
  },

};
</script>

<style>
/**
 * Override default Jds-Checkbox styling
 */
.site-filter .jds-checkbox__option-label {
  font-size: 14px !important;
  font-weight: normal !important;
  color: #424242 !important;
}
</style>
